import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Newspaper, User, Mail, Phone, MapPin, Globe, 
  FileText, Send, Loader2, Check, Instagram,
  Twitter, Youtube, ArrowRight, Type, List, ToggleLeft,
  Users, BarChart3, DollarSign, Image, Plus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import api, { settingsApi } from '../lib/api';

const MEDIA_TYPES = [
  { value: 'difusion', label: 'Difusión / Medios' },
  { value: 'fotografia', label: 'Fotografía' },
  { value: 'video', label: 'Video / Filmación' },
  { value: 'produccion', label: 'Producción' },
  { value: 'catering', label: 'Catering' },
  { value: 'decoracion', label: 'Decoración' },
  { value: 'sonido', label: 'Sonido / DJ' },
  { value: 'seguridad', label: 'Seguridad' },
  { value: 'logistica', label: 'Logística' },
  { value: 'marketing', label: 'Marketing Digital' },
  { value: 'otro', label: 'Otro Servicio' },
];

const SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'tiktok', label: 'TikTok', icon: Globe },
  { value: 'twitter', label: 'Twitter/X', icon: Twitter },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'facebook', label: 'Facebook', icon: Globe },
  { value: 'website', label: 'Sitio Web', icon: Globe },
];

const INTERESTS = [
  'Electrónica', 'Reggaeton', 'Rock', 'Cumbia', 'Trap', 'Pop',
  'Hip Hop', 'Indie', 'Techno', 'House', 'Latino', 'Comercial'
];

const PressApplicationPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formConfig, setFormConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    media_name: '',  // Changed to service/company name
    contact_name: '',
    email: '',
    phone: '',
    province: '',
    city: '',
    description: '',
    media_type: 'difusion',  // Now represents service type
    message: '',
    social_networks: [{ platform: 'instagram', url: '', followers: 0, username: '' }],
    audience: {
      age_range_min: 18,
      age_range_max: 35,
      gender_distribution: 'mixto',
      locations: [],
      interests: []
    },
    statistics: {
      average_reach: 0,
      engagement_rate: 0,
      average_impressions: 0,
      average_story_views: 0,
      monthly_reach: 0
    },
    service_packages: []
  });

  useEffect(() => {
    const loadFormConfig = async () => {
      try {
        const response = await api.get('/settings/press-form');
        if (response.data && response.data.fields) {
          setFormConfig(response.data);
        }
      } catch (error) {
        console.error('Error loading form config:', error);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadFormConfig();
  }, []);

  const heroContent = {
    badge: formConfig?.hero?.badge || 'Portal de Servicios',
    title: formConfig?.hero?.title || 'Ofrecé tus servicios',
    subtitle: formConfig?.hero?.subtitle || 'Conectá con productoras de eventos y ofrecé tus servicios profesionales. Llegá a más clientes y generá ingresos con tu trabajo.'
  };

  const benefits = formConfig?.benefits || [
    { title: 'Conexión Directa', description: 'Acceso a productoras buscando difusión' },
    { title: 'Tus Precios', description: 'Definí tus paquetes y tarifas' },
    { title: 'Pagos Seguros', description: 'Cobrá a través de la plataforma' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSocialNetworkChange = (index, field, value) => {
    const updated = [...formData.social_networks];
    updated[index] = { ...updated[index], [field]: field === 'followers' ? parseInt(value) || 0 : value };
    setFormData(prev => ({ ...prev, social_networks: updated }));
  };

  const addSocialNetwork = () => {
    setFormData(prev => ({
      ...prev,
      social_networks: [...prev.social_networks, { platform: 'instagram', url: '', followers: 0, username: '' }]
    }));
  };

  const removeSocialNetwork = (index) => {
    if (formData.social_networks.length > 1) {
      setFormData(prev => ({
        ...prev,
        social_networks: prev.social_networks.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAudienceChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      audience: { ...prev.audience, [field]: value }
    }));
  };

  const toggleInterest = (interest) => {
    const current = formData.audience.interests || [];
    const updated = current.includes(interest)
      ? current.filter(i => i !== interest)
      : [...current, interest];
    handleAudienceChange('interests', updated);
  };

  const handleStatChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      statistics: { ...prev.statistics, [field]: parseFloat(value) || 0 }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.media_name || !formData.email) {
      toast.error('Por favor completá los campos requeridos');
      return;
    }

    setLoading(true);

    try {
      await api.post('/press/apply', formData);
      setSubmitted(true);
      toast.success('¡Solicitud enviada correctamente!');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <SEO title="Solicitud Enviada - Portal de Servicios" />
        <Navbar />
        <div className="min-h-[70vh] flex items-center justify-center px-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md text-center"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-green-500" size={40} />
            </div>
            <h1 className="text-3xl font-black text-white mb-4">¡Solicitud Enviada!</h1>
            <p className="text-zinc-400 mb-8">
              Revisaremos tu solicitud y te contactaremos pronto. 
              Recibirás un email con las credenciales de acceso una vez aprobada.
            </p>
            <Button onClick={() => window.location.href = '/'} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
              Volver al Inicio
            </Button>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Portal de Servicios - Bravo Producciones"
        description="Ofrecé tus servicios profesionales a productoras de eventos"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Newspaper className="text-blue-400" size={16} />
              <span className="text-blue-400 text-sm font-medium">{heroContent.badge}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
              {heroContent.title}
            </h1>
            <p className="text-lg text-zinc-400 mb-8">
              {heroContent.subtitle}
            </p>
          </motion.div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center"
              >
                <h3 className="text-white font-bold mb-2">{benefit.title}</h3>
                <p className="text-zinc-400 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl p-8"
          >
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-4 mb-8">
              {[1, 2, 3].map((s) => (
                <button
                  key={s}
                  onClick={() => setStep(s)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    step === s 
                      ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400' 
                      : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <span className="w-6 h-6 rounded-full bg-current/20 flex items-center justify-center text-sm font-bold">
                    {s}
                  </span>
                  <span className="text-sm font-medium hidden sm:inline">
                    {s === 1 && 'Datos Básicos'}
                    {s === 2 && 'Redes y Audiencia'}
                    {s === 3 && 'Estadísticas'}
                  </span>
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Basic Data */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <User className="text-blue-400" size={20} />
                    Datos del Servicio
                  </h2>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Nombre de Empresa/Servicio *</label>
                      <Input
                        name="media_name"
                        value={formData.media_name}
                        onChange={handleChange}
                        placeholder="Ej: Juan Fotografía"
                        className="bg-zinc-800 border-zinc-700"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Tipo de Servicio</label>
                      <select
                        name="media_type"
                        value={formData.media_type}
                        onChange={handleChange}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      >
                        {MEDIA_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Nombre de Contacto</label>
                      <Input
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        placeholder="Tu nombre"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Email *</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="contacto@tuservicio.com"
                        className="bg-zinc-800 border-zinc-700"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Teléfono</label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+54 11 1234-5678"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Provincia</label>
                      <Input
                        name="province"
                        value={formData.province}
                        onChange={handleChange}
                        placeholder="Buenos Aires"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm text-zinc-400 mb-2">Descripción del Servicio</label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Contanos sobre tu medio, qué tipo de contenido publicás, tu estilo..."
                        className="bg-zinc-800 border-zinc-700 min-h-[100px]"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      type="button" 
                      onClick={() => setStep(2)}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Siguiente
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Social Networks & Audience */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <Instagram className="text-blue-400" size={20} />
                    Redes Sociales
                  </h2>

                  <div className="space-y-4">
                    {formData.social_networks.map((network, index) => (
                      <div key={index} className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
                        <div className="grid md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">Plataforma</label>
                            <select
                              value={network.platform}
                              onChange={(e) => handleSocialNetworkChange(index, 'platform', e.target.value)}
                              className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm"
                            >
                              {SOCIAL_PLATFORMS.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">Usuario</label>
                            <Input
                              value={network.username}
                              onChange={(e) => handleSocialNetworkChange(index, 'username', e.target.value)}
                              placeholder="@usuario"
                              className="bg-zinc-700 border-zinc-600 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">Seguidores</label>
                            <Input
                              type="number"
                              value={network.followers}
                              onChange={(e) => handleSocialNetworkChange(index, 'followers', e.target.value)}
                              placeholder="10000"
                              className="bg-zinc-700 border-zinc-600 text-sm"
                            />
                          </div>
                          <div className="flex items-end">
                            {formData.social_networks.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSocialNetwork(index)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addSocialNetwork}
                      className="w-full border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-500"
                    >
                      <Plus size={16} className="mr-2" />
                      Agregar Red Social
                    </Button>
                  </div>

                  <h2 className="text-xl font-bold text-white mt-8 mb-6 flex items-center gap-2">
                    <Users className="text-blue-400" size={20} />
                    Audiencia / Target
                  </h2>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Rango de Edad</label>
                      <div className="flex items-center gap-4">
                        <Input
                          type="number"
                          value={formData.audience.age_range_min}
                          onChange={(e) => handleAudienceChange('age_range_min', parseInt(e.target.value) || 18)}
                          className="bg-zinc-800 border-zinc-700 w-24"
                          placeholder="18"
                        />
                        <span className="text-zinc-500">a</span>
                        <Input
                          type="number"
                          value={formData.audience.age_range_max}
                          onChange={(e) => handleAudienceChange('age_range_max', parseInt(e.target.value) || 35)}
                          className="bg-zinc-800 border-zinc-700 w-24"
                          placeholder="35"
                        />
                        <span className="text-zinc-500">años</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Género predominante</label>
                      <select
                        value={formData.audience.gender_distribution}
                        onChange={(e) => handleAudienceChange('gender_distribution', e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                      >
                        <option value="mixto">Mixto</option>
                        <option value="masculino">Mayoría Masculino</option>
                        <option value="femenino">Mayoría Femenino</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-3">Intereses de la audiencia</label>
                    <div className="flex flex-wrap gap-2">
                      {INTERESTS.map(interest => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => toggleInterest(interest)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            formData.audience.interests?.includes(interest)
                              ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400'
                              : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                      Atrás
                    </Button>
                    <Button 
                      type="button" 
                      onClick={() => setStep(3)}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Siguiente
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Statistics */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <BarChart3 className="text-blue-400" size={20} />
                    Estadísticas y Métricas
                  </h2>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Alcance promedio por post</label>
                      <Input
                        type="number"
                        value={formData.statistics.average_reach}
                        onChange={(e) => handleStatChange('average_reach', e.target.value)}
                        placeholder="5000"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Engagement Rate (%)</label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.statistics.engagement_rate}
                        onChange={(e) => handleStatChange('engagement_rate', e.target.value)}
                        placeholder="3.5"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Impresiones promedio</label>
                      <Input
                        type="number"
                        value={formData.statistics.average_impressions}
                        onChange={(e) => handleStatChange('average_impressions', e.target.value)}
                        placeholder="10000"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">Vistas de Stories promedio</label>
                      <Input
                        type="number"
                        value={formData.statistics.average_story_views}
                        onChange={(e) => handleStatChange('average_story_views', e.target.value)}
                        placeholder="3000"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm text-zinc-400 mb-2">Alcance mensual total</label>
                      <Input
                        type="number"
                        value={formData.statistics.monthly_reach}
                        onChange={(e) => handleStatChange('monthly_reach', e.target.value)}
                        placeholder="50000"
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Mensaje adicional (opcional)</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="¿Algo más que quieras contarnos?"
                      className="bg-zinc-800 border-zinc-700 min-h-[100px]"
                    />
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button type="button" variant="ghost" onClick={() => setStep(2)}>
                      Atrás
                    </Button>
                    <Button 
                      type="submit"
                      disabled={loading}
                      className="bg-blue-500 hover:bg-blue-600 min-w-[200px]"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send size={16} className="mr-2" />
                          Enviar Solicitud
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PressApplicationPage;
